import type {DateRange} from "../../common/newDatePicker/datePickerShared";

export type InsightsDateRangePreset =
    | "today"
    | "yesterday"
    | "thisWeek"
    | "last7Days"
    | "lastWeek"
    | "thisMonth"
    | "thisYear"
    | "last30Days"
    | "allTime";

/**
 * Where "all time" starts. A fixed floor rather than a query: no organisation has survey
 * data from before this, so a range from here to today lists everything, and the picker
 * keeps its one contract of always holding a real range.
 */
export const ALL_TIME_START = new Date(2020, 0, 1);

function localDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value: Date, days: number): Date {
    const result = new Date(value);
    result.setDate(result.getDate() + days);
    return result;
}

function mondayOfWeek(value: Date): Date {
    const dayFromMonday = (value.getDay() + 6) % 7;
    return addDays(value, -dayFromMonday);
}

export function insightsRangeForPreset(preset: InsightsDateRangePreset, now = new Date()): DateRange {
    const today = localDay(now);
    const thisMonday = mondayOfWeek(today);

    switch (preset) {
        case "today":
            return {start: today, end: new Date(today)};
        case "yesterday": {
            const yesterday = addDays(today, -1);
            return {start: yesterday, end: new Date(yesterday)};
        }
        case "thisWeek":
            return {start: thisMonday, end: addDays(thisMonday, 6)};
        case "last7Days":
            return {start: addDays(today, -6), end: today};
        case "lastWeek":
            return {start: addDays(thisMonday, -7), end: addDays(thisMonday, -1)};
        case "thisMonth":
            return {
                start: new Date(today.getFullYear(), today.getMonth(), 1),
                end: new Date(today.getFullYear(), today.getMonth() + 1, 0),
            };
        case "thisYear":
            return {
                start: new Date(today.getFullYear(), 0, 1),
                end: new Date(today.getFullYear(), 11, 31),
            };
        case "last30Days":
            return {start: addDays(today, -29), end: today};
        case "allTime":
            return {start: new Date(ALL_TIME_START), end: today};
    }
}

const INSIGHTS_DATE_RANGE_PRESETS: readonly InsightsDateRangePreset[] = [
    "today",
    "yesterday",
    "thisWeek",
    "last7Days",
    "lastWeek",
    "thisMonth",
    "thisYear",
    "last30Days",
    "allTime",
];

const sameDay = (left: Date, right: Date) => localDay(left).getTime() === localDay(right).getTime();

/**
 * The preset a range currently stands for, or null for a hand-picked one. Derived rather
 * than remembered, so a range restored from a URL still reads as "Last 30 days" and a
 * preset that has drifted past midnight stops claiming to be one.
 */
export function presetForRange(range: DateRange, now = new Date()): InsightsDateRangePreset | null {
    return INSIGHTS_DATE_RANGE_PRESETS.find(preset => {
        const candidate = insightsRangeForPreset(preset, now);
        return sameDay(candidate.start, range.start) && sameDay(candidate.end, range.end);
    }) ?? null;
}

export const createDefaultInsightsRange = (): DateRange => insightsRangeForPreset("last30Days");

/**
 * Relative mode (`InsightsDateRangeControl` with `relative`): a period that moves with every run of a
 * scheduled report, stored as a preset rather than dates. Mirrors the API's `ReportPeriod`
 * (`service/automation/report/ReportPeriod.kt`); change both together. Every preset ends with a whole
 * day before the reference day (the run's): the rolling ones end the day before it, the calendar ones
 * are the week (Monday to Sunday), month or quarter before the one it is in.
 */
export type RelativeDateRangePreset =
    | "yesterday"
    | "lastWeek"
    | "last7Days"
    | "last30Days"
    | "lastMonth"
    | "lastQuarter"
    | "lastNDays";

export const RELATIVE_DATE_RANGE_PRESETS: readonly RelativeDateRangePreset[] = [
    "yesterday",
    "lastWeek",
    "last7Days",
    "last30Days",
    "lastMonth",
    "lastQuarter",
    "lastNDays",
];

export const MIN_RELATIVE_DAYS = 1;
export const MAX_RELATIVE_DAYS = 366;

export type RelativeComparison = "previousPeriod" | "previousYear";

export interface RelativeDateRangeSelection {
    preset: RelativeDateRangePreset;
    /** "lastNDays" only; a stored value outside 1–366 is kept so the field can show it. */
    days?: number;
    /** Null = no comparison. */
    comparison: RelativeComparison | null;
}

/** Whole days within 1–366, else undefined. */
export function relativeDays(value: unknown): number | undefined {
    const days = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
    return Number.isInteger(days) && days >= MIN_RELATIVE_DAYS && days <= MAX_RELATIVE_DAYS ? days : undefined;
}

function rollingDays(lastDay: Date, days: number): DateRange {
    return {start: addDays(lastDay, -(days - 1)), end: lastDay};
}

function quarterStartMonth(month: number): number {
    return Math.floor(month / 3) * 3;
}

/** The days a preset covers for a run on `referenceDay`; null for "lastNDays" without valid days. */
export function relativeRangeForPreset(
    preset: RelativeDateRangePreset,
    days?: number,
    referenceDay = new Date(),
): DateRange | null {
    const today = localDay(referenceDay);
    const yesterday = addDays(today, -1);

    switch (preset) {
        case "yesterday":
            return rollingDays(yesterday, 1);
        case "lastWeek": {
            const thisMonday = mondayOfWeek(today);
            return {start: addDays(thisMonday, -7), end: addDays(thisMonday, -1)};
        }
        case "last7Days":
            return rollingDays(yesterday, 7);
        case "last30Days":
            return rollingDays(yesterday, 30);
        case "lastMonth":
            return {
                start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
                end: new Date(today.getFullYear(), today.getMonth(), 0),
            };
        case "lastQuarter": {
            const first = quarterStartMonth(today.getMonth()) - 3;
            return {
                start: new Date(today.getFullYear(), first, 1),
                end: new Date(today.getFullYear(), first + 3, 0),
            };
        }
        case "lastNDays": {
            const valid = relativeDays(days);
            return valid === undefined ? null : rollingDays(yesterday, valid);
        }
    }
}

/** A year earlier on the same date; 29 February becomes the 28th (java.time's `minusYears`). */
function sameDateLastYear(value: Date): Date {
    const year = value.getFullYear() - 1;
    const lastDayOfMonth = new Date(year, value.getMonth() + 1, 0).getDate();
    return new Date(year, value.getMonth(), Math.min(value.getDate(), lastDayOfMonth));
}

/**
 * What a relative period is compared with: the same days a year earlier, or the period of the same
 * kind right before it — the month or quarter before a calendar one, the same number of days before
 * any other.
 */
export function relativeComparisonRange(
    preset: RelativeDateRangePreset,
    range: DateRange,
    comparison: RelativeComparison,
): DateRange {
    if (comparison === "previousYear") {
        return {start: sameDateLastYear(range.start), end: sameDateLastYear(range.end)};
    }

    const start = localDay(range.start);
    switch (preset) {
        case "lastMonth":
            return {
                start: new Date(start.getFullYear(), start.getMonth() - 1, 1),
                end: new Date(start.getFullYear(), start.getMonth(), 0),
            };
        case "lastQuarter":
            return {
                start: new Date(start.getFullYear(), start.getMonth() - 3, 1),
                end: new Date(start.getFullYear(), start.getMonth(), 0),
            };
        default: {
            const length = Math.round((localDay(range.end).getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
            return rollingDays(addDays(start, -1), length);
        }
    }
}
